import { useMediaQuery, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { api, isDefined, isTruthy, parseError, useToast } from 'common';

import { type AgenticChatViewMode, useGlobalStateContext } from '../../GlobalState/context';

interface AgenticChatProps {
  agenticChatMode: 'Enabled' | 'Disabled';
  drawerSizing: {
    minimumDrawerWidthInPixels: number;
    reservedWidthInPixels: number;
  };
}

interface AgenticChatSelectionOptions {
  sessionId: string | undefined;
  chatNavigationCount?: number;
}

/** Controls Flow AI visibility, conversation selection, and the view mode. */
export function useAgenticChat(props: AgenticChatProps) {
  const { agenticChatMode, drawerSizing } = props;
  const { agenticChatViewMode, setAgenticChatViewMode } = useGlobalStateContext();
  const theme = useTheme();
  const isNarrowViewport = useMediaQuery(
    theme.breakpoints.down(
      drawerSizing.minimumDrawerWidthInPixels + drawerSizing.reservedWidthInPixels
    ),
    { noSsr: true }
  );
  const location = useLocation();
  const toast = useToast();
  const [chatState, setChatState] = useState<{
    agenticChatVisibility: 'Unopened' | 'Closed' | 'Open';
    sessionId: string | undefined;
    overlayDismissalCount: number;
    chatNavigationCount: number;
  }>({
    agenticChatVisibility: 'Unopened',
    sessionId: undefined,
    overlayDismissalCount: 0,
    chatNavigationCount: 0,
  });
  const [previousLocation, setPreviousLocation] = useState(location);
  const viewMode: AgenticChatViewMode = isTruthy(isNarrowViewport)
    ? 'Fullscreen'
    : agenticChatViewMode;

  function closeAgenticChat() {
    setChatState((current) => ({
      ...current,
      agenticChatVisibility: 'Closed',
      overlayDismissalCount: current.overlayDismissalCount + 1,
      chatNavigationCount: current.chatNavigationCount + 1,
    }));
  }

  function selectSession(options: AgenticChatSelectionOptions) {
    const { sessionId, chatNavigationCount } = options;
    setChatState((current) =>
      isDefined(chatNavigationCount) && chatNavigationCount !== current.chatNavigationCount
        ? current
        : {
            ...current,
            sessionId,
            agenticChatVisibility: 'Open',
            chatNavigationCount: current.chatNavigationCount + 1,
          }
    );
  }

  const createSessionMutation = useMutation({
    mutationFn: () => api.client.AgenticPlatformSession.create({}),
    onSuccess: (session, options: { chatNavigationCount: number }) => {
      // Skipped if the chat was closed, navigated away from in fullscreen, or replaced while pending.
      selectSession({ sessionId: session.id, chatNavigationCount: options.chatNavigationCount });
    },
    retry: false,
    onError: (error) => toast.showError(`Could not start chat: ${parseError(error)}`),
  });

  function startNewChat() {
    createSessionMutation.mutate({ chatNavigationCount: chatState.chatNavigationCount });
  }

  // A fullscreen chat covers the page, so navigating away closes it to reveal the new page.
  if (location !== previousLocation) {
    setPreviousLocation(location);
    if (chatState.agenticChatVisibility === 'Open' && viewMode === 'Fullscreen') {
      closeAgenticChat();
    }
  }

  return {
    agenticChatMode,
    drawerSizing,
    isSidebarAvailable: !isTruthy(isNarrowViewport),
    agenticChatVisibility:
      agenticChatMode === 'Enabled'
        ? chatState.agenticChatVisibility
        : chatState.agenticChatVisibility === 'Unopened'
          ? 'Unopened'
          : 'Closed',
    sessionId: chatState.sessionId,
    overlayDismissalCount: chatState.overlayDismissalCount,
    chatNavigationCount: chatState.chatNavigationCount,
    viewMode,
    closeAgenticChat,
    toggleAgenticChatVisibility: () => {
      if (chatState.agenticChatVisibility === 'Open' && agenticChatMode === 'Enabled') {
        closeAgenticChat();
        return;
      }
      setChatState((current) => ({ ...current, agenticChatVisibility: 'Open' }));
      if (!isDefined(chatState.sessionId) && !createSessionMutation.isPending) {
        startNewChat();
      }
    },
    createSessionMutation,
    startNewChat,
    selectSession,
    clearDeletedSession: (deletedSessionId: string) => {
      setChatState((current) => ({
        ...current,
        sessionId: current.sessionId === deletedSessionId ? undefined : current.sessionId,
      }));
    },
    setViewMode: (nextViewMode: AgenticChatViewMode) => setAgenticChatViewMode(nextViewMode),
  };
}

export type AgenticChatController = ReturnType<typeof useAgenticChat>;
